"use client";

import React from "react";
import Link from "next/link";
import { useConvexAuth } from "convex/react";
import { useBoard } from "@/components/BoardProvider";
import { useAuthModal } from "@/auth/auth-modal-provider";

/**
 * Closing CTA: accent band, editorial split layout — copy left, action right —
 * with the board name as a giant script watermark. No image assets; adapts to
 * any board's accent automatically.
 */
export function ClosingCta() {
  const board = useBoard();
  const { openAuthModal } = useAuthModal();
  const { isAuthenticated } = useConvexAuth();

  return (
    <section className="mx-auto max-w-6xl px-6 py-14 md:py-20">
      <div className="relative overflow-hidden rounded-[2rem] bg-primary">
        {/* Giant script watermark */}
        <span
          aria-hidden
          className="pointer-events-none absolute -bottom-10 -right-4 select-none font-script italic leading-none text-white/[0.08]"
          style={{ fontSize: "clamp(8rem, 24vw, 17rem)" }}
        >
          {board.shortName.toLowerCase()}
        </span>

        <div className="relative flex flex-col items-start gap-8 px-8 py-14 md:flex-row md:items-center md:justify-between md:px-14 md:py-16">
          <div className="max-w-xl text-left">
            <h2 className="font-polysans text-3xl leading-tight text-white sm:text-4xl">
              Revise alongside students chasing the same offer
            </h2>
            <p className="mt-4 font-polysans text-base font-light leading-relaxed text-white/80 md:text-lg">
              With our lifetime pass you get access to original mocks, papers
              and a statistical analysis of your results so far to help you reach
              the top percentile - where a good tutor costs £60 for a single
              hour.
              <span className="mt-3 block">
                If you're still not sure, sign up free, no card needed :)
              </span>
            </p>
          </div>

          <div className="shrink-0">
            {isAuthenticated ? (
              <Link
                href="/dashboard"
                className="inline-block rounded-full bg-white px-8 py-3.5 font-polysans text-sm font-medium text-primary shadow-lg transition-transform duration-150 ease-[cubic-bezier(0.23,1,0.32,1)] hover:-translate-y-0.5 active:scale-[0.97]"
              >
                Go to your dashboard
              </Link>
            ) : (
              <button
                type="button"
                onClick={() => openAuthModal("signup")}
                className="rounded-full bg-white px-8 py-3.5 font-polysans text-sm font-medium text-primary shadow-lg transition-transform duration-150 ease-[cubic-bezier(0.23,1,0.32,1)] hover:-translate-y-0.5 active:scale-[0.97]"
              >
                Get started free
              </button>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
